
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

const MarketAnalyticsPage = () => {
  const features = [
    {
      icon: 'ri-line-chart-line',
      title: 'Predictive Analytics',
      description: 'AI forecasts market trends and property values up to 12 months in advance.'
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Neighborhood Insights',
      description: 'Deep analysis of local market conditions, demographics, and growth patterns.'
    },
    {
      icon: 'ri-home-line',
      title: 'Comparable Analysis',
      description: 'Intelligent comp selection and analysis for accurate property valuations.'
    },
    {
      icon: 'ri-notification-line',
      title: 'Market Alerts',
      description: 'Real-time notifications about market changes and investment opportunities.'
    },
    {
      icon: 'ri-pie-chart-line',
      title: 'Portfolio Analytics',
      description: 'Track and analyze your entire property portfolio performance.'
    },
    {
      icon: 'ri-file-chart-line',
      title: 'Custom Reports',
      description: 'Generate professional market reports for clients and presentations.'
    }
  ];

  const analyticsTypes = [
    {
      title: 'Price Trend Analysis',
      description: 'Track historical price movements and predict future trends',
      image: 'https://public.readdy.ai/ai/img_res/b1c5ddbacbaba3a3f2006274f1ba12f4.jpg',
      metrics: ['Price History', 'Growth Rates', 'Seasonal Patterns', 'Market Cycles']
    },
    {
      title: 'Market Demand Forecasting',
      description: 'Predict buyer demand and optimal listing timing',
      image: 'https://readdy.ai/api/search-image?query=market%20demand%20forecasting%20dashboard%20with%20AI%20predictions%2C%20real%20estate%20analytics%20interface%2C%20data%20visualization%20charts&width=500&height=300&seq=analytics2&orientation=landscape',
      metrics: ['Demand Indicators', 'Inventory Levels', 'Days on Market', 'Absorption Rates']
    },
    {
      title: 'Investment Opportunity Scoring',
      description: 'AI-powered scoring of investment potential for properties',
      image: 'https://readdy.ai/api/search-image?query=investment%20opportunity%20scoring%20interface%20with%20property%20ratings%2C%20AI%20analysis%20dashboard%2C%20real%20estate%20investment%20analytics&width=500&height=300&seq=analytics3&orientation=landscape',
      metrics: ['ROI Potential', 'Risk Assessment', 'Cash Flow Analysis', 'Appreciation Forecast']
    }
  ];

  const benefits = [
    {
      icon: 'ri-eye-line',
      title: 'Market Visibility',
      description: 'Get insights your competitors don\'t have access to',
      stat: '360°'
    },
    {
      icon: 'ri-time-line',
      title: 'Faster Decisions',
      description: 'Make data-driven decisions in minutes, not days',
      stat: '10x'
    },
    {
      icon: 'ri-target-line',
      title: 'Accurate Pricing',
      description: 'Price properties within 2% of optimal market value',
      stat: '98%'
    },
    {
      icon: 'ri-trophy-line',
      title: 'Competitive Edge',
      description: 'Stay ahead with predictive market intelligence',
      stat: '#1'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative py-20 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(37, 99, 235, 0.8), rgba(37, 99, 235, 0.8)), url('https://readdy.ai/api/search-image?query=modern%20real%20estate%20analytics%20dashboard%20with%20AI%20market%20data%2C%20professional%20business%20intelligence%20interface%2C%20charts%20and%20graphs%20display&width=1920&height=1080&seq=market1&orientation=landscape')`
        }}
      >
        <div className="container mx-auto px-6 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-6">
              AI Market Analytics
            </h1>
            <p className="text-xl mb-8 text-blue-100">
              Predict market trends, identify opportunities, and make data-driven decisions with advanced AI analytics
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              <button className="bg-white text-blue-600 px-8 py-3 rounded-lg font-bold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
                View Live Demo
              </button>
              <button className="border-2 border-white text-white px-8 py-3 rounded-lg font-bold hover:bg-white hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer">
                Get Market Report
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-gray-800">Why Market Analytics Matter</h2>
            <p className="text-xl text-gray-600">Gain the competitive advantage with AI-powered insights</p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center bg-white p-8 rounded-xl shadow-lg">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${benefit.icon} text-2xl text-blue-600`}></i>
                </div>
                <div className="text-3xl font-bold text-blue-600 mb-3">{benefit.stat}</div>
                <h3 className="text-xl font-bold mb-3 text-gray-800">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-gray-800">Advanced Analytics Features</h2>
            <p className="text-xl text-gray-600">Comprehensive market intelligence at your fingertips</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                  <i className={`${feature.icon} text-2xl text-blue-600`}></i>
                </div>
                <h3 className="text-xl font-bold mb-4 text-gray-800">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Analytics Types */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-gray-800">Types of Analytics</h2>
            <p className="text-xl text-gray-600">Comprehensive market analysis across multiple dimensions</p>
          </div>
          <div className="space-y-16">
            {analyticsTypes.map((type, index) => (
              <div key={index} className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <img 
                    src={type.image} 
                    alt={type.title}
                    className="w-full h-80 object-cover object-top rounded-xl shadow-lg"
                  />
                </div>
                <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                  <h3 className="text-3xl font-bold mb-4 text-gray-800">{type.title}</h3>
                  <p className="text-xl text-gray-600 mb-6">{type.description}</p>
                  <div className="grid grid-cols-2 gap-4">
                    {type.metrics.map((metric, metricIndex) => (
                      <div key={metricIndex} className="flex items-center">
                        <i className="ri-check-line text-green-500 mr-2"></i>
                        <span className="text-gray-700">{metric}</span>
                      </div>
                    ))}
                  </div>
                  <button className="mt-6 bg-blue-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer">
                    Learn More
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Live Dashboard Preview */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-gray-800">Live Market Dashboard</h2>
            <p className="text-xl text-gray-600">Real-time market data and analytics</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              <div className="text-center p-6 bg-green-50 rounded-lg">
                <div className="text-3xl font-bold text-green-600 mb-2">+12.5%</div>
                <div className="text-gray-600">Market Growth</div>
                <div className="text-sm text-gray-500">vs. last quarter</div>
              </div>
              <div className="text-center p-6 bg-blue-50 rounded-lg">
                <div className="text-3xl font-bold text-blue-600 mb-2">18 Days</div>
                <div className="text-gray-600">Avg. Days on Market</div>
                <div className="text-sm text-gray-500">-3 days vs. last month</div>
              </div>
              <div className="text-center p-6 bg-purple-50 rounded-lg">
                <div className="text-3xl font-bold text-purple-600 mb-2">$485K</div>
                <div className="text-gray-600">Median Price</div>
                <div className="text-sm text-gray-500">+8.2% vs. last year</div>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-6">
              <h4 className="text-lg font-bold mb-4 text-gray-800">Market Trend Visualization</h4>
              <div className="h-64 bg-white rounded-lg flex items-center justify-center">
                <div className="text-center text-gray-500">
                  <i className="ri-bar-chart-line text-4xl mb-4"></i>
                  <p>Interactive charts and graphs would appear here</p>
                  <p className="text-sm">Real-time market data visualization</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-gray-800">Use Cases</h2>
            <p className="text-xl text-gray-600">How real estate professionals use market analytics</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-home-heart-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold mb-4 text-gray-800">Listing Agents</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Optimal pricing strategies</li>
                <li>• Best timing for listings</li>
                <li>• Market positioning advice</li>
                <li>• Competitive analysis</li>
              </ul>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-search-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold mb-4 text-gray-800">Buyer Agents</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Investment opportunity identification</li>
                <li>• Neighborhood comparisons</li>
                <li>• Future value predictions</li>
                <li>• Market timing advice</li>
              </ul>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-building-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold mb-4 text-gray-800">Investors</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Portfolio performance tracking</li>
                <li>• Market entry/exit timing</li>
                <li>• Risk assessment</li>
                <li>• ROI optimization</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6">Get Market Intelligence Today</h2>
          <p className="text-xl mb-8 text-blue-100">
            Make smarter decisions with AI-powered market analytics
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button className="bg-white text-blue-600 px-8 py-3 rounded-lg font-bold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
              Start Free Trial
            </button>
            <button className="border-2 border-white text-white px-8 py-3 rounded-lg font-bold hover:bg-white hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer">
              Request Demo
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MarketAnalyticsPage;
